import type {
  CockpitFiscalQuarter,
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPlanningPeriod,
} from '../../model/types'

export const OVERALL_OFFERING = 'Overall'

/** Offering-independent initiatives belong to every offering, but stay one record in Overall. */
export const isCockpitInitiativeInOffering = (
  initiative: Pick<ICockpitKeyInitiative, 'ProgramType'>,
  selectedOffering: string
): boolean => {
  const offering = initiative.ProgramType?.trim()

  return !offering || selectedOffering === OVERALL_OFFERING || offering === selectedOffering.trim()
}

/**
 * Quarters the selected period resolves to, or `null` for "every quarter" — which is both
 * `fullYear` and a YTD with no known current quarter. Shared by the initiatives and the MDF
 * activities so one period selection means the same thing on both sub-tabs.
 */
export const getSelectedCockpitQuarters = (
  selectedPeriods: CockpitPeriodKey[],
  currentQuarter?: CockpitFiscalQuarter
): CockpitFiscalQuarter[] | null => {
  if (selectedPeriods.includes('fullYear')) {
    return null
  }

  if (selectedPeriods.includes('ytd')) {
    return currentQuarter
      ? Array.from({ length: currentQuarter }, (_, index) => (index + 1) as CockpitFiscalQuarter)
      : null
  }

  return selectedPeriods.flatMap((period) => {
    const match = /^q([1-4])$/.exec(period)

    return match ? [Number(match[1]) as CockpitFiscalQuarter] : []
  })
}

/** Quarters an initiative covers, parsed from its `Period` (`"Q1,Q2,Q4"`). */
export const getInitiativeQuarters = (period: string): CockpitFiscalQuarter[] =>
  Array.from(
    new Set(
      Array.from(period.matchAll(/Q([1-4])/gi), (match) => Number(match[1]) as CockpitFiscalQuarter)
    )
  )

/**
 * Share of an initiative's Goal/Incremental that belongs to the selected external period.
 * Initiative amounts are annual totals spread equally across every quarter in `Period`.
 */
export const getInitiativePeriodAllocation = (
  period: string,
  selectedPeriods: CockpitPeriodKey[],
  currentQuarter?: CockpitFiscalQuarter
): number => {
  const selectedQuarters = getSelectedCockpitQuarters(selectedPeriods, currentQuarter)

  if (selectedQuarters === null) {
    return 1
  }

  const initiativeQuarters = getInitiativeQuarters(period)

  if (!initiativeQuarters.length) {
    return 0
  }

  const quartersInScope = initiativeQuarters.filter((quarter) =>
    selectedQuarters.includes(quarter)
  ).length

  return quartersInScope / initiativeQuarters.length
}

interface IParams {
  initiatives: ICockpitKeyInitiative[]
  fYear: number
  selectedOffering: string
  selectedCategory: string | null
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
}

/**
 * The initiatives the tile is showing: the selected cockpit fiscal year's, scoped to the offering
 * (`Overall` keeps them all) and overlapping the selected quarters. An initiative spanning
 * `Q1,Q2` shows under a `Q2` selection — it is being worked on in that quarter.
 */
export const getCockpitInitiativesInScope = ({
  initiatives,
  fYear,
  selectedOffering,
  selectedCategory,
  selectedPeriods,
  planningPeriod,
}: IParams): ICockpitKeyInitiative[] => {
  const selectedQuarters = getSelectedCockpitQuarters(selectedPeriods, planningPeriod?.fQuarter)

  return initiatives.filter((initiative) => {
    if (initiative.FYear !== fYear) {
      return false
    }

    if (!isCockpitInitiativeInOffering(initiative, selectedOffering)) {
      return false
    }

    if (selectedCategory !== null && initiative.BusinessUnit !== selectedCategory) {
      return false
    }

    if (selectedQuarters === null) {
      return true
    }

    return getInitiativeQuarters(initiative.Period).some((quarter) =>
      selectedQuarters.includes(quarter)
    )
  })
}
